import {ChangeDetectionStrategy, Component, computed, effect, EventEmitter, Injectable, input, Output, signal} from "@angular/core";
import {toSignal} from "@angular/core/rxjs-interop";
import {TranslateModule} from "@ngx-translate/core";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MatButton} from "@angular/material/button";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from "@angular/material/autocomplete";
import {AvtaleId, AvtaledetaljerModell, AvtaleRessursClient, EnkelBrukerModell, RedigerAvtalepesifikasjon} from "../../../../../felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {i18n} from "../../../../../felles-ng2/tjenester/i18n/Oversettelser";
import {asynkArbeidPågårSignal} from "../../../../../felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {InlineMeldingKomponent} from "../../../../delt/komponenter/inline-melding/inline-melding";

@Component({
    selector: 'rediger-avtale',
    templateUrl: './rediger-avtale.html',
    styleUrls: ['./rediger-avtale.scss'],
    imports: [
        TranslateModule,
        ReactiveFormsModule,
        MatButton,
        MatFormField,
        MatInput,
        MatLabel,
        MatAutocomplete,
        MatAutocompleteTrigger,
        MatOption,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RedigerAvtaleKomponent {
    static ajsSelector = 'redigerAvtale';

    avtale = input.required<AvtaledetaljerModell>();
    @Output() vedOppdatert = new EventEmitter<AvtaledetaljerModell>();
    valgtBruker = signal<EnkelBrukerModell | null>(null);
    lagrer = asynkArbeidPågårSignal();
    oppdatert = signal(false);

    avtalenavn = new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng});
    sokeTekst = new FormControl<string>('');
    private sokeTekstSignal = toSignal(this.sokeTekst.valueChanges, {initialValue: ''});

    tilgjengeligeBrukere = computed(() => {
        const avtale = this.avtale();
        const kontaktperson = avtale.kontaktperson;
        const aktiveBrukere = avtale.aktiveBrukere;
        if (aktiveBrukere.some(b => b.id === kontaktperson.id)) return aktiveBrukere;
        return [kontaktperson, ...aktiveBrukere];
    });

    filtrerteBrukere = computed(() => {
        const brukere = this.tilgjengeligeBrukere();
        const signalVerdi = this.sokeTekstSignal();
        if (typeof signalVerdi !== 'string') return brukere;
        const tekst = signalVerdi.toLowerCase();
        if (!erIkkeTomStreng(tekst)) return brukere;
        return brukere.filter(b =>
            b.navnEllerEpost.toLowerCase().includes(tekst) ||
            b.epost.toLowerCase().includes(tekst)
        );
    });

    constructor(private avhengigheter: RedigerAvtaleKomponentAvhengigheter) {
        effect(() => {
            const avtale = this.avtale();
            // this.gjeldende.set(avtale);
            this.avtalenavn.setValue(avtale.navn);
        });
    }

    visNavn(bruker: EnkelBrukerModell | null): string {
        return bruker?.navnEllerEpost ?? '';
    }

    brukerValgt(bruker: EnkelBrukerModell): void {
        this.valgtBruker.set(bruker);
        this.oppdatert.set(false);
    }

    async lagreKontaktperson(): Promise<void> {
        const avtale = this.avtale();
        const bruker = this.valgtBruker() ?? avtale.kontaktperson;

        await this.lagrer.utfør(async () => {
            const oppdatert = await this.avhengigheter.redigerAvtale(avtale.id, {
                navn: this.avtalenavn.value,
                kontaktperson: bruker.id
            });
            // this.gjeldende.set(oppdatert);
            this.avtalenavn.setValue(oppdatert.navn);
            this.vedOppdatert.emit(oppdatert);
            this.oppdatert.set(true);
            this.valgtBruker.set(null);
            this.sokeTekst.setValue('');
        });
    }
}

@Injectable()
export class RedigerAvtaleKomponentAvhengigheter {

    constructor(private avtaleRessurs: AvtaleRessursClient) {
    }

    redigerAvtale(avtaleId: AvtaleId, spesifikasjon: RedigerAvtalepesifikasjon): Promise<AvtaledetaljerModell> {
        return this.avtaleRessurs.oppdater(avtaleId, spesifikasjon).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke oppdatere kontaktperson'));
    }
}
